import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectBpm, selectIsPlaying } from "../store";
import { ParameterSliderRange } from "../parameterWidgets/ParameterSliderRange";
import { quantizeCeil, quantizeRound } from "../utils/math";
import { getSampleLibraryManagerInstance } from '../SamplePlayer';
import { Sound } from "../../datatypes";

function ResponsiveCanvas({onResize=()=>{}, ref}) {
    const innerRef = useRef();
    ref ??= innerRef;

    function adjustSize() {
        const {width, height} = ref.current.parentElement.getBoundingClientRect();
        onResize({width, height});
        ref.current.width = width;
        ref.current.height = height;
    }
    useEffect(() => {
        if (!ref.current) return;
        const observer = new ResizeObserver(adjustSize);
        observer.observe(ref.current.parentElement);
        adjustSize();
        return () => observer.disconnect();
    }, [ref.current])

    return <canvas ref={ref} style={{width: "100%", height: "100%", display: "block"}}/>

}


export function Waveform({soundName, isReverse, start=0, end=1, color="white", onResize: _onResize, className}) {
    const canvasRef = useRef();

    const clear = () => canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    function redraw(buffer, start, end, isReverse) {
        const ctx = canvasRef.current.getContext('2d');
        const { width, height } = canvasRef.current;
        let bufferData = buffer.getChannelData( 0 );
        bufferData = bufferData.slice(Math.floor(start * bufferData.length), Math.floor(end * bufferData.length));

        if (isReverse) bufferData = bufferData.toReversed();
        ctx.clearRect(0, 0, width, height);
        drawWaveform(ctx, bufferData, width, height, color);
    }
    const [dummyRedrawState, setDummyRedrawState] = useState(0)
    function onResize(props) {
        setDummyRedrawState(p => p+1);
       _onResize && _onResize(props);
    }
    useEffect(() => {
        clear();
        let cancel = false;
        getSampleLibraryManagerInstance().getBufferForSound(soundName, buffer => cancel || redraw(buffer, start, end, isReverse));
        return () => cancel = true;
    }, [soundName, color, start, end, isReverse, dummyRedrawState]);

    return <div className={className}>
        <ResponsiveCanvas onResize={onResize} ref={canvasRef} />
    </div>
}

export function PlayheadCanvas({getPlayheadPosition, className, onResize}) {
    const canvasRef = useRef();
    
    function drawPlayhead() {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        let playheadPosition = getPlayheadPosition();
        if (playheadPosition == null) return;
        if (!Array.isArray(playheadPosition)) playheadPosition = [playheadPosition];
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 2;
        ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
        ctx.shadowBlur = 4;
        playheadPosition.forEach(position => {
            if (position == null) return;
            const x = position * canvas.width;
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, canvas.height);
            ctx.stroke();
        })
        ctx.shadowBlur = 0;
    }
    
    const animationFrameIdRef = useRef();
    function loop() {
        drawPlayhead();
        animationFrameIdRef.current = requestAnimationFrame(loop);
    };
    const startLoop = () => requestAnimationFrame(loop);
    function stopLoop() {
        if (animationFrameIdRef.current == null) return;
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
    };

      useEffect(() => {
        // Not checking isPlaying because sampler samples can be triggered while Transport is off
        startLoop();
        return stopLoop;
    }, [getPlayheadPosition]);

    return <div className={className}>
        <ResponsiveCanvas onResize={onResize} ref={canvasRef} />
    </div>
    
}


export const LooperWaveform = ({instrument, className}) => {
    const currentClip = useSelector(instrument.getDisplayClipSelector());
    const { start, end, speed, isReverse, soundName } = {...Sound.getDefaults(), ...currentClip.sound};
    const clipLength = useSelector(instrument.getDisplayClipLengthSelector());
    const isPlaying = useSelector(selectIsPlaying);
    const bpm = useSelector(selectBpm);
    
    const [buffer, setBuffer] = useState(undefined);
    
    const canvasRef = useRef(null);
    const animationFrameIdRef = useRef(null);

    useEffect(() => {
        if (!soundName) return setBuffer(undefined);
        getSampleLibraryManagerInstance().getBufferForSound(soundName, buffer => setBuffer(buffer));
    }, [soundName, instrument]);
    
    const drawWaveformOverlay = (startPosition, endPosition, showBeatPositions) => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        const bufferDuration = buffer?.duration ?? 0;

        const beatDurationSeconds = (60 / bpm) * speed
        const stepDurationSeconds = ((60 / bpm) * speed) / 4;
        const pixelsPerSecond = bufferDuration > 0 ? canvas.width / bufferDuration : 0;
        const pixelsPerStep = pixelsPerSecond * stepDurationSeconds;

        // Quantize startX and endX to the nearest pixelsPerStep multiple
        const startX = quantizeRound(startPosition * canvas.width, pixelsPerStep);
        const endX = quantizeRound(endPosition * canvas.width, pixelsPerStep);

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
        ctx.fillRect(0, 0, startX, canvas.height);
        ctx.fillRect(endX, 0, canvas.width - endX, canvas.height);

        if (showBeatPositions && bufferDuration > 0) {            
            const numberOfBeats = Math.floor(bufferDuration / beatDurationSeconds);
            
            ctx.strokeStyle = 'rgba(160, 160, 160, 0.7)';
            ctx.lineWidth = 1;
            for (let beatIndex = 0; beatIndex <= numberOfBeats; beatIndex++) {
                const beatPosition = (beatIndex * beatDurationSeconds) / bufferDuration;
                const beatX = beatPosition * canvas.width;
                ctx.beginPath();
                ctx.moveTo(beatX, 0);
                ctx.lineTo(beatX, canvas.height);
                ctx.stroke();
            }

            // Show the number of beats covered in the loop part at the top right corner
            const effectiveNumberOfBeats = clipLength / 4;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.font = '14px Arial';
            ctx.textAlign = 'right';
            ctx.textBaseline = 'top';
            ctx.fillText(`${effectiveNumberOfBeats} beats`, canvas.width - 10, 5);
        }
    };
    const [dummyRedrawState, setDummyRedrawState] = useState(0)
    const onResize = () => setDummyRedrawState(p => p+1);
    useEffect(() => {
        drawWaveformOverlay(start, end, true);
    }, [instrument, buffer, start, end, speed, bpm, dummyRedrawState]);

    return (
        <div className={`modul-border ${className} flex flex-col h-full`} style={{position: 'relative', overflow: 'hidden'}}>
            {buffer?.loaded && <>
                <div className="waveform-display flex-1 stack" style={{position: 'relative', minHeight: 0}}>  {/* minHeight 0 to prevent flex item from expanding due to responsivecanvas*/}
                    <Waveform soundName={soundName} isReverse={isReverse} />
                    <PlayheadCanvas getPlayheadPosition={() => instrument.audioNodes.samplePlayer?.position ?? 0}/>
                    <ResponsiveCanvas ref={canvasRef} style={{pointerEvents: "none"}} onResize={onResize}/>
                </div>
                <ParameterSliderRange instrument={instrument} parameterHandlers={[instrument.getParameterHandler('start'), instrument.getParameterHandler('end')]} sideEffect={() => instrument.adjustClipLengthToFit()} noLabel noOutput style={{margin: "20px 0"}}/>
            </>}
        </div>
    );
};

// Code here was inspired from: https://github.com/meandavejustice/draw-wave/blob/master/index.js
function drawWaveform(canvasCtx, bufferData, width, height, color) {
    canvasCtx.fillStyle = color;
    var step = Math.ceil( bufferData.length / width );
    var amp = height / 2;
    for(var i=0; i < width; i++){
        var min = 1.0;
        var max = -1.0;
        for (var j=0; j<step; j++) {
            var datum = bufferData[(i*step)+j];
            if (datum < min)
                min = datum;
            if (datum > max)
                max = datum;
        }
        canvasCtx.fillRect(i,(1+min)*amp,1,Math.max(1,(max-min)*amp));
    }
}